import type { AvailableCollector } from '@/api/vorderingen';
import { m2BasisLabel, type M2BasisSource } from '@/utils/m2Basis';
import { fotoCount, interventieFaseIdFromKey } from '@/utils/interventiePhotos';

export { fotoCount };

export interface InterventiePhotoGroup {
  /** yyyymmdd of the upload day. */
  date: string;
  /** dd-mm-yyyy. */
  label: string;
  count: number;
  keys: string[];
  /** Selection id — `{repair}:{yyyymmdd}`, unique per repair AND day. */
  id?: string;
  /** The repair: the fase of an id-keyed key; null = older (pile / name-keyed). */
  faseId?: number | null;
  /** What the row and the client's omschrijving call it (see titleInterventieGroups). */
  title?: string;
}

/** yyyy-mm-dd → dd/mm (compact planned-date badge). */
export const fmtDateShort = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const [, m, d] = String(iso).slice(0, 10).split('-');
  return d && m ? `${d}/${m}` : String(iso);
};

export function naturalCompare(a: string, b: string): number {
  return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
}

/**
 * A vordering percentage is a WHOLE percent, everywhere.
 *
 * Odoo invoices a sale order line off `qty_delivered`, which is 2 decimals on a
 * quantity of exactly 1 — so a milestone of 5.5 % is delivered as 0.06 and billed
 * as 6 %. Whole percent is the only precision that survives to the invoice, and
 * the API rounds to it at the request boundary. These helpers keep the preview
 * showing the same number the client is actually billed; without them the portal
 * would promise 5.5 % next to a euro figure Odoo will not produce.
 */
export const wholePercent = (pct: number): number => Math.round(pct);

/** The headroom ceiling FLOORS to a whole percent, mirroring the API, so a
 *  rounded-up claim can never exceed what the line still has open. */
export const floorWholePercent = (pct: number): number => Math.floor(pct);

/**
 * The whole percent an einde-werken close-out bills on a line. Mirrors the API's
 * einde-werken branch (`calculateTotals` in portal-api/src/routes/vorderingen.ts),
 * which is the authority — this exists so the preview shows the figure that will
 * actually be billed.
 *
 * Round to the nearest whole percent: prior vorderingen rounded their euros to
 * cents, so the remaining sits a hair below an exact whole percent and flooring
 * would strand a full 1 % of the line. But rounding UP may only absorb that cent
 * drift — materially over the remaining means the line was never whole-percent
 * aligned and cannot close, so it floors instead of billing above the line.
 */
export function closeOutWholePercent(remaining: number, lineAmount: number): number {
  if (lineAmount <= 0) return 0;
  const exact = (Math.max(0, remaining) / lineAmount) * 100;
  const rounded = wholePercent(exact);
  const CENT_SLACK = 0.05;
  const billed = Math.round((rounded / 100) * lineAmount * 100) / 100;
  return billed > Math.max(0, remaining) + CENT_SLACK ? floorWholePercent(exact) : rounded;
}

export function buildOmschrijving(
  selected: AvailableCollector[],
  legplanM2: number,
  isEinde: boolean,
  markedFaseNames: string[] = [],
  interventieGroups: InterventiePhotoGroup[] = [],
  /** Which m² legplanM2 actually is, so the line the client reads names the right
   *  surface instead of claiming a verlegplan that was never drawn. */
  m2Basis: M2BasisSource = 'legplan',
  /** «m² mee op factuur»: off leaves every m² out, the Totaal line included. */
  showM2 = false,
  /** «LP mee op factuur»: off leaves the collector's LPs off its line. */
  showLp = false,
): string {
  const lines: string[] = [];
  if (isEinde) lines.push('EINDE WERKEN');
  if (markedFaseNames.length > 0) {
    lines.push(`Fases: ${markedFaseNames.join(', ')}`);
    lines.push('');
  }

  if (selected.length > 0) {
    const blokMap = new Map<string, Map<string, AvailableCollector[]>>();
    for (const c of selected) {
      const blok = c.blok || 'Hoofdgebouw';
      const verd = c.verdieping || '+00';
      if (!blokMap.has(blok)) blokMap.set(blok, new Map());
      const verdMap = blokMap.get(blok)!;
      if (!verdMap.has(verd)) verdMap.set(verd, []);
      verdMap.get(verd)!.push(c);
    }

    let totalM2 = 0;
    const sortedBloks = Array.from(blokMap.keys()).sort(naturalCompare);
    for (const blok of sortedBloks) {
      const showBlok = blok !== 'Hoofdgebouw';
      if (showBlok) lines.push(`Blok ${blok}`);
      const verdMap = blokMap.get(blok)!;
      const sortedVerd = Array.from(verdMap.keys()).sort(naturalCompare);
      for (const verd of sortedVerd) {
        lines.push(showBlok ? `  ${verd}` : verd);
        const list = verdMap.get(verd)!.sort((a, b) => naturalCompare(a.name, b.name));
        for (const c of list) {
          const parts: string[] = [showBlok ? `    ${c.name}` : `  ${c.name}`];
          const sys = c.systeem || '';
          if (sys) parts.push(sys);
          // A mixed-LP collector carries its LPs on its own line ("LP10, LP12");
          // the invoice text lists collectors only, never their kringen.
          if (showLp && c.legpatroon) parts.push(c.legpatroon);
          if (showM2 && c.collector_m2 > 0) {
            parts.push(`${c.collector_m2.toFixed(1)} m²`);
            totalM2 += c.collector_m2;
          }
          if (c.installateur) parts.push(c.installateur);
          lines.push(parts.join(' — '));
        }
      }
    }

    if (totalM2 > 0) {
      lines.push('');
      if (legplanM2 > 0) {
        const pct = wholePercent((totalM2 / legplanM2) * 100);
        lines.push(`Totaal: ${totalM2.toFixed(1)} m² (${pct}% van ${m2BasisLabel(m2Basis)})`);
      } else {
        lines.push(`Totaal: ${totalM2.toFixed(1)} m²`);
      }
    }
  }

  if (interventieGroups.length > 0) {
    if (lines.length > 0) lines.push('');
    for (const g of interventieGroups) {
      lines.push(`${g.title ?? `Interventie ${g.label}`} (${fotoCount(g.count)})`);
    }
  }

  return lines.join('\n');
}

export interface SystemGroup {
  system: string;
  totalM2: number;
  blokGroups: Array<{
    blok: string;
    verdiepingen: Array<{
      verdieping: string;
      collectors: AvailableCollector[];
      totalM2: number;
    }>;
  }>;
}

export function groupBySystem(selected: AvailableCollector[]): SystemGroup[] {
  const sysMap = new Map<string, Map<string, Map<string, AvailableCollector[]>>>();
  for (const c of selected) {
    const sys = c.systeem || '(Geen systeem)';
    const blok = c.blok || 'Hoofdgebouw';
    const verd = c.verdieping || '+00';
    if (!sysMap.has(sys)) sysMap.set(sys, new Map());
    const blokMap = sysMap.get(sys)!;
    if (!blokMap.has(blok)) blokMap.set(blok, new Map());
    const verdMap = blokMap.get(blok)!;
    if (!verdMap.has(verd)) verdMap.set(verd, []);
    verdMap.get(verd)!.push(c);
  }

  const result: SystemGroup[] = [];
  const sortedSys = Array.from(sysMap.keys()).sort(naturalCompare);
  for (const sys of sortedSys) {
    const blokMap = sysMap.get(sys)!;
    const sortedBloks = Array.from(blokMap.keys()).sort(naturalCompare);
    const blokGroups: SystemGroup['blokGroups'] = [];
    let sysTotal = 0;
    for (const blok of sortedBloks) {
      const verdMap = blokMap.get(blok)!;
      const sortedVerd = Array.from(verdMap.keys()).sort(naturalCompare);
      const verdiepingen: SystemGroup['blokGroups'][number]['verdiepingen'] = [];
      for (const verd of sortedVerd) {
        const collectors = verdMap.get(verd)!.sort((a, b) => naturalCompare(a.name, b.name));
        const totalM2 = collectors.reduce((sum, c) => sum + (c.collector_m2 || 0), 0);
        sysTotal += totalM2;
        verdiepingen.push({ verdieping: verd, collectors, totalM2 });
      }
      blokGroups.push({ blok, verdiepingen });
    }
    result.push({ system: sys, totalM2: sysTotal, blokGroups });
  }
  return result;
}

export function groupInterventieByDate(photos: Array<{ key: string }>): InterventiePhotoGroup[] {
  const byDate = new Map<string, string[]>();
  for (const p of photos) {
    const filename = p.key.split('/').pop() || '';
    const datePart = filename.slice(0, 8);
    if (!/^\d{8}$/.test(datePart)) continue;
    if (!byDate.has(datePart)) byDate.set(datePart, []);
    byDate.get(datePart)!.push(p.key);
  }
  return Array.from(byDate.entries())
    .sort((a, b) => b[0].localeCompare(a[0]))
    .map(([date, keys]) => ({
      date,
      label: `${date.slice(6, 8)}-${date.slice(4, 6)}-${date.slice(0, 4)}`,
      count: keys.length,
      keys,
    }));
}

/**
 * Interventie photos grouped per REPAIR and DAY (Igor, 2026-09-29) — what a PL
 * selects into a client vordering. The repair is the fase of an id-keyed key
 * (`…/interventie/fase-{id}/…`); every older key (the project pile, name-keyed
 * folders) is one "older" repair. Undated keys are dropped, as in
 * groupInterventieByDate. Newest day first; within a day, repairs before the
 * older pile, then by fase id — stable across reloads.
 *
 * Billing still goes key-exact (`interventie_photo_keys`, `interventie_vordered`),
 * so this changes what is SELECTED together, never what a key is worth.
 */
export function groupInterventieByRepair(photos: Array<{ key: string }>): InterventiePhotoGroup[] {
  const groups = new Map<string, InterventiePhotoGroup>();
  for (const p of photos) {
    const filename = p.key.split('/').pop() || '';
    const date = filename.slice(0, 8);
    if (!/^\d{8}$/.test(date)) continue;
    const faseId = interventieFaseIdFromKey(p.key);
    const id = `${faseId === null ? 'oud' : `fase-${faseId}`}:${date}`;
    let g = groups.get(id);
    if (!g) {
      g = { id, date, faseId, label: `${date.slice(6, 8)}-${date.slice(4, 6)}-${date.slice(0, 4)}`, count: 0, keys: [] };
      groups.set(id, g);
    }
    g.keys.push(p.key);
    g.count = g.keys.length;
  }
  const rank = (g: InterventiePhotoGroup) => (g.faseId == null ? Number.MAX_SAFE_INTEGER : g.faseId);
  return [...groups.values()].sort((a, b) => b.date.localeCompare(a.date) || rank(a) - rank(b));
}

/**
 * Give each group the name the row and the client's omschrijving show. A project
 * without any id-keyed photo keeps the old wording ("Interventie dd-mm-yyyy");
 * otherwise a repair reads "Interventie – {fase} · dd-mm-yyyy" and the rest
 * "Oudere interventies · dd-mm-yyyy". Mirrors portal-api interventie-vordering.ts,
 * which names the foto-rapport / ZIP sections the same way.
 */
export function titleInterventieGroups(
  groups: InterventiePhotoGroup[],
  fases: Array<{ id: number; name: string }>,
): InterventiePhotoGroup[] {
  const hasRepairs = groups.some((g) => g.faseId != null);
  return groups.map((g) => {
    if (!hasRepairs) return { ...g, title: `Interventie ${g.label}` };
    if (g.faseId == null) return { ...g, title: `Oudere interventies · ${g.label}` };
    const naam = fases.find((f) => f.id === g.faseId)?.name ?? `Fase ${g.faseId}`;
    return { ...g, title: `Interventie – ${naam} · ${g.label}` };
  });
}
